import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { PlannerAction, PlannerState, Waypoint } from '../types';
import { makeWaypointId, MAX_WAYPOINTS } from '../plannerReducer';
import {
  downloadFile,
  parseGeoJSON,
  parseGpx,
  safeFilename,
  toGeoJSON,
  toGpx,
  toKml,
} from '../io/serialize';
import { buildPermalink } from '../io/permalink';
import {
  SavedRoute,
  deleteSavedRoute,
  listSavedRoutes,
  saveRoute,
  updateSavedRoute,
} from '../api/routingApi';

const GOLD = '#f5b942';

const Action: React.FC<{ onClick: () => void; disabled?: boolean; accent?: boolean; children: React.ReactNode }> = ({
  onClick, disabled, accent, children,
}) => (
  <Typography
    variant="caption"
    onClick={disabled ? undefined : onClick}
    sx={{
      fontWeight: 700,
      cursor: disabled ? 'default' : 'pointer',
      color: disabled ? 'text.disabled' : accent ? GOLD : 'text.primary',
      '&:hover': disabled ? undefined : { textDecoration: 'underline' },
    }}
  >
    {children}
  </Typography>
);

interface RouteFilesDialogProps {
  open: boolean;
  onClose: () => void;
  state: PlannerState;
  dispatch: React.Dispatch<PlannerAction>;
  missionId?: number | null;
  defaultName?: string;
}

const RouteFilesDialog: React.FC<RouteFilesDialogProps> = ({
  open,
  onClose,
  state,
  dispatch,
  missionId,
  defaultName,
}) => {
  const [saved, setSaved] = useState<SavedRoute[]>([]);
  const [name, setName] = useState(defaultName || '');
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const route = state.routes[state.primaryIndex] || null;
  const exportInput = {
    name: name.trim() || defaultName || 'Planned route',
    waypoints: state.waypoints,
    route,
    profile: state.options.profile,
  };

  useEffect(() => {
    if (!open) return;
    setNote(null);
    setError(null);
    listSavedRoutes(missionId ?? undefined)
      .then(setSaved)
      .catch(() => setSaved([]));
  }, [open, missionId]);

  const handleImport = async (file: File) => {
    setError(null);
    try {
      const text = await file.text();
      const parsed = file.name.toLowerCase().endsWith('.gpx')
        ? parseGpx(text, MAX_WAYPOINTS)
        : parseGeoJSON(text, MAX_WAYPOINTS);
      const waypoints: Waypoint[] = parsed.coordinates.map((c) => ({
        id: makeWaypointId(),
        longitude: c.longitude,
        latitude: c.latitude,
        label: c.label,
        labelStatus: c.label ? 'done' : 'idle',
        source: 'import',
      }));
      dispatch({ type: 'SET_WAYPOINTS', waypoints, resetHistory: true });
      if (parsed.name && !name.trim()) setName(parsed.name);
      setNote(`Imported ${waypoints.length} waypoints from ${file.name}.`);
    } catch (e: any) {
      setError(e?.message || 'Could not read that file.');
    }
  };

  const persist = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = {
        name: exportInput.name,
        profile: state.options.profile,
        options: { exclude: state.options.exclude, depart_at: state.options.departAt },
        waypoints: state.waypoints.map((w) => ({
          longitude: w.longitude,
          latitude: w.latitude,
          label: w.label,
        })),
        geometry: route?.geometry || null,
        stats: (route ? { distance: route.distance, duration: route.duration } : {}) as Record<string, number>,
        mission_id: missionId ?? null,
      };
      const result = state.savedRouteId
        ? await updateSavedRoute(state.savedRouteId, body)
        : await saveRoute(body);
      dispatch({ type: 'MARK_SAVED', savedRouteId: result.id });
      setSaved(await listSavedRoutes(missionId ?? undefined));
      setNote(`Saved as “${result.name}”.`);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Could not save the route.');
    } finally {
      setBusy(false);
    }
  };

  const load = (row: SavedRoute) => {
    const waypoints: Waypoint[] = (row.waypoints || []).map((w) => ({
      id: makeWaypointId(),
      longitude: w.longitude,
      latitude: w.latitude,
      label: w.label,
      labelStatus: w.label ? 'done' : 'idle',
      source: 'saved',
    }));
    dispatch({
      type: 'RESET',
      waypoints,
      options: {
        profile: row.profile as any,
        exclude: (row.options as any)?.exclude,
        departAt: (row.options as any)?.depart_at ?? null,
      },
    });
    dispatch({ type: 'MARK_SAVED', savedRouteId: row.id });
    setName(row.name);
    onClose();
  };

  const copyLink = async () => {
    // Always point at /missions: it is the one page that decodes planner
    // params on mount, and the planner can be opened from several others.
    const link = buildPermalink(
      state.waypoints,
      state.options,
      `${window.location.origin}/missions`,
    );
    try {
      await navigator.clipboard.writeText(link);
      setNote('Permalink copied. It restores waypoints, profile, and options.');
    } catch {
      setError(link);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.25 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800, flex: 1 }}>Route files</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </Box>
      <Divider />
      <DialogContent sx={{ pt: 2 }}>
        <TextField
          fullWidth
          size="small"
          label="Route name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mb: 1.5 }}
        />

        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>Export</Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
          <Action
            disabled={state.waypoints.length === 0}
            onClick={() => downloadFile(
              `${safeFilename(exportInput.name)}.geojson`,
              JSON.stringify(toGeoJSON(exportInput), null, 2),
              'application/geo+json',
            )}
          >
            GeoJSON
          </Action>
          <Action
            disabled={state.waypoints.length === 0}
            onClick={() => downloadFile(
              `${safeFilename(exportInput.name)}.gpx`,
              toGpx(exportInput),
              'application/gpx+xml',
            )}
          >
            GPX
          </Action>
          <Action
            disabled={state.waypoints.length === 0}
            onClick={() => downloadFile(
              `${safeFilename(exportInput.name)}.kml`,
              toKml(exportInput),
              'application/vnd.google-earth.kml+xml',
            )}
          >
            KML
          </Action>
          <Action disabled={state.waypoints.length === 0} onClick={copyLink}>Copy link</Action>
        </Box>

        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>Import</Typography>
        <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
          <Action onClick={() => fileRef.current?.click()}>Choose a GPX or GeoJSON file</Action>
          <input
            ref={fileRef}
            type="file"
            accept=".gpx,.json,.geojson,application/geo+json"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) handleImport(file);
            }}
          />
        </Box>

        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>Saved routes</Typography>
        <Box sx={{ mb: 1 }}>
          <Action accent disabled={busy || state.waypoints.length < 2} onClick={persist}>
            {state.savedRouteId ? 'Update saved route' : 'Save this route'}
          </Action>
        </Box>
        {saved.length === 0 && (
          <Typography variant="caption" color="text.secondary">Nothing saved yet.</Typography>
        )}
        {saved.map((row) => (
          <Box key={row.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
            <Typography
              variant="caption"
              onClick={() => load(row)}
              noWrap
              sx={{ flex: 1, cursor: 'pointer', '&:hover': { color: GOLD } }}
            >
              {row.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem' }}>
              {(row.waypoints || []).length} wp · {row.profile.replace('-', ' ')}
            </Typography>
            <IconButton
              size="small"
              onClick={async () => {
                await deleteSavedRoute(row.id);
                setSaved(await listSavedRoutes(missionId ?? undefined));
                if (state.savedRouteId === row.id) dispatch({ type: 'MARK_SAVED', savedRouteId: null });
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        ))}

        {note && (
          <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: GOLD }}>{note}</Typography>
        )}
        {error && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1.5, wordBreak: 'break-all' }}>
            {error}
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RouteFilesDialog;
